<template>
  <profile-edit-section-card
    v-if="localState"
    title="Experience"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    :has-invalid-input="hasInvalidInput"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <poppins-text-field
      id="years-experience"
      v-model="localState.yearsExperience"
      label-name="Years of paid companion care experience"
      type="text"
      pattern="[0-9]*"
      inputmode="numeric"
      :limit-input-to-pattern="true"
      :helper-text="yearsExperienceHelperText"
      :status="yearsExperienceStatus"
      class="mb-24"
    />
    <fieldset class="mb-24">
      <legend class="form__label pb-8">What age groups do you have experience with?</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <ul>
        <li v-for="option in ageGroupOptions" :key="option.value" class="mb-8">
          <poppins-checkbox v-model="localState.ageGroupsExperience" :label-name="option.label" :value="option.value" />
        </li>
      </ul>
    </fieldset>
    <fieldset class="mb-24">
      <legend class="form__label pb-8">What basic care services do you have experience providing?</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <ul>
        <li v-for="option in basicExperienceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox v-model="localState.basicCareServicesExperience" :label-name="option.label" :value="option.value" />
        </li>
      </ul>
    </fieldset>
    <fieldset>
      <legend class="form__label pb-8">What personal care services do you have experience providing?</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <ul>
        <li v-for="option in personalExperienceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox
            v-model="localState.personalCareServicesExperience"
            :label-name="option.label"
            :value="option.value"
          />
        </li>
      </ul>
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsTextField from '@/components/textfield/TextField.vue';
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import {
  COMPANION_CARE_AGE_GROUP_OPTIONS,
  COMPANION_CARE_BASIC_EXPERIENCE_OPTIONS,
  COMPANION_CARE_PERSONAL_EXPERIENCE_OPTIONS,
} from '../../__types__/CareTypeFormOptions';
import type { CompanionCareProfileFormState } from '../../__types__/CompanionCareProfileTypes';
import HelperStatus from '@/helpers/status';

const MIN_YEARS_EXPERIENCE = 1;
const MAX_YEARS_EXPERIENCE = 50;

export default {
  name: 'CompanionCareExperienceForm',
  components: {
    ProfileEditSectionCard,
    PoppinsTextField,
    PoppinsCheckbox,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      hasInvalidInput: false,
      yearsExperienceStatus: HelperStatus.Neutral,
    };
  },
  computed: {
    ageGroupOptions: () => COMPANION_CARE_AGE_GROUP_OPTIONS,
    basicExperienceOptions: () => COMPANION_CARE_BASIC_EXPERIENCE_OPTIONS,
    personalExperienceOptions: () => COMPANION_CARE_PERSONAL_EXPERIENCE_OPTIONS,
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
    yearsExperienceHelperText(): string {
      if (!this.hasInvalidInput) return '';
      if (!this.localState?.yearsExperience && this.localState?.yearsExperience !== 0) {
        return 'Years of experience is required';
      }
      if (isNaN(Number(this.localState.yearsExperience)) || Number(this.localState.yearsExperience) < MIN_YEARS_EXPERIENCE) {
        return 'Years of experience must be at least 0';
      }
      if (Number(this.localState.yearsExperience) > MAX_YEARS_EXPERIENCE) {
        return 'Years of experience cannot be over 50';
      }
      return '';
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
    'localState.yearsExperience': function (newValue) {
      if (newValue && this.yearsExperienceStatus === HelperStatus.Error) {
        this.yearsExperienceStatus = HelperStatus.Neutral;
        this.hasInvalidInput = false;
      }
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as CompanionCareProfileFormState | null;
      if (!state) return;

      this.localState = {
        yearsExperience: String(state.yearsExperience ?? 0),
        ageGroupsExperience: [...(state.ageGroupsExperience || [])],
        basicCareServicesExperience: [...(state.basicCareServicesExperience || [])],
        personalCareServicesExperience: [...(state.personalCareServicesExperience || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    isYearsExperienceInvalid(): boolean {
      if (!this.localState) return true;
      const years = Number(this.localState.yearsExperience);
      return (
        this.localState.yearsExperience === '' ||
        this.localState.yearsExperience === null ||
        this.localState.yearsExperience === undefined ||
        isNaN(years) ||
        years < MIN_YEARS_EXPERIENCE ||
        years > MAX_YEARS_EXPERIENCE
      );
    },
    async handleSave() {
      if (!this.localState) return;
      if (this.isYearsExperienceInvalid()) {
        this.yearsExperienceStatus = HelperStatus.Error;
        this.hasInvalidInput = true;
        this.$nextTick(() => {
          document.getElementById('years-experience')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }
      if (this.hasUnsavedChanges) {
        await this.saveSection({
          yearsExperience: Number(this.localState.yearsExperience),
          ageGroups: [...this.localState.ageGroupsExperience],
          experience: [...this.localState.basicCareServicesExperience, ...this.localState.personalCareServicesExperience],
        });
      }
    },
  },
};
</script>
